import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { HelperService } from 'src/app/services/utils/helper.service';
import { ApiService } from 'src/app/services/api/api.service';
import { SessionService } from '../../../services/session/session.service';
import { CONSTANTS, OPERATION, COMPONENTS } from 'src/app/dataModels/constants';
import { FacilityService } from '../../facility/facility.service';
import { SESSION_KEYS } from '../../../dataModels/session-keys';
import { lastValueFrom } from 'rxjs';
import { CONFIG } from 'src/app/dataModels/config';
import { MonkeypoxService } from '../../monkey-pox/monkeypox.service';

@Component({
  selector: 'app-monkeypox-agreement',
  templateUrl: './monkeypox-agreement.component.html',
  styleUrls: ['./monkeypox-agreement.component.scss'],
})
export class MonkeypoxAgreementComponent implements OnInit {
  /**
   * Form group for Vaccine Coordinators
   */
  monkeypoxAgreeFrm: FormGroup;
  /**
   * constructor to inject dependency
   * @param apiService instance of apiService
   * @param helperService instance of helperService
   * @param compAccessService instance of component access service
   * @param resourceManagerService instance of resourceManagerService
   */
  constructor(
    private apiService: ApiService,
    private facilityService: FacilityService,
    private helperService: HelperService,
    private monkeyPoxService: MonkeypoxService
  ) {}

  /**
   * Facility Users list
   */
  facilityUsersList = [];

  /**
   * List of users for Primary Coordinators
   */
  primaryUsersList;

  /**
   * Existing Monkeypox Agreement
   */
  existingMonkeypoxAgree;

  /**
   * agreeresponse
   */
  agreeresponse;
  /**
   * indicate if user has access to page
   */
  canUserAccessMPAgree;
  /**
   * List of users for Backup Coordinators
   */
  backupUsersList = [];
  /**
   * boolean indicator to indicate form is submitted, and to run validations
   */
  isSubmit = false;

  /**
   * Indicator to display busy loading
   */
  isBusy;

  /**
   * Action is default to ADD, will be set to Review during Review
   */
  @Input() action = CONSTANTS.ADD;
  /**
   * Facility Id
   */
  @Input() facilityId;
  /**
   * boolean indicator, to determine if component is loading to fetch data
   */
  isLoading = false;

  /**
   * Disable Form
   */
  @Input() disableForm: boolean = false;

  /**
   * logged in user
   */
  @Input() user;

  /**
   * is user admin
   */
  isUserAdmin;

  /**
   * facilityName
   */
  @Input() facilityName;

  /**
   * indicate if fields are required
   */
  isRequired = false;

  /**
   * Originally saved Vaccine Coordinator values
   */
  origVaccCoordinator = {
    primaryUserIdSeq: '',
    backupUserIdSeq: '',
  };

  /**
   * Initialize the component
   * Initialize the form controls
   */
  ngOnInit(): void {
    this.initFormControls();
    this.initComponent();
    this.checkUserAccess();
  }

  /**
   * check user access
   */
  async checkUserAccess() {
    await this.monkeyPoxService.checkAccess().then((response) => {
      this.canUserAccessMPAgree = response[COMPONENTS.FORMS.MONKEYPOX_AGREEMENT] !== CONSTANTS.DENIED;
    });
    if (this.canUserAccessMPAgree) {
      this.initAgreementModel();
      this.getMonkeyPoxProviderAgreement();
    }
  }

  /**
   * Form Data to store the Vaccines Coorindators component form values
   */
  formData = {
    isFormModified: false,
    data: {
      primaryUserIdSeq: '',
      backupUserIdSeq: '',
      agreement_date: this.helperService.formatDate(new Date()),
    } as any,
    isError: false,
  };

  /**
   * Initialize the form controls
   */
  initFormControls() {
    this.monkeypoxAgreeFrm = new FormGroup({
      provider_seq: new FormControl(''),
      agreement_flag: new FormControl(false, Validators.requiredTrue),
      agreeInit: new FormControl({ value: '', disabled: true }),
      agreement_date: new FormControl({ value: '', disabled: true }),
      primaryMonkeypoxCoordinator: new FormControl('', Validators.required),
      backupMonkeypoxCoordinator: new FormControl('', Validators.required),
    });
  }
  initAgreementModel = function () {
    this.agreement = {
      provider_seq: '',
      agreement_flag: false,
      agreeInit: this.user.firstName + ' ' + this.user.lastName,
      agreement_date: this.helperService.formatDate(new Date()),
    };
    this.f.agreeInit.setValue(this.user.firstName + ' ' + this.user.lastName);
    this.f.agreement_date.setValue(this.helperService.formatDate(new Date()));
  };
  /**
   * Initialize component - lazy loaded
   * Get list of Facility users
   * Get Primary and Backup coordinators originally saved data
   */
  async initComponent() {
    this.isLoading = true;
    await this.getFacilityUsers();
    this.isLoading = false;
  }

  /**
   * Getter for the Vaccine coordinators form controls
   */
  get f() {
    return this.monkeypoxAgreeFrm?.controls;
  }

  /**
   * Check if the Ctrl is invalid
   * @param ctrl ctrl to check invalid for
   */
  isControlInvalid(ctrl: AbstractControl) {
    return this.helperService.isControlInvalid(this.isSubmit, ctrl);
  }

  /**
   * Get List of Facility Users
   */
  async getFacilityUsers() {
    this.isBusy = await this.facilityService.getFacilityUsers(this.facilityId).then((response) => {
      this.facilityUsersList = response.data.filter(
        (x) =>
          (x.status === 'NEW' || x.status === 'ACTIVE') && ['FACILITY USER', 'EHDI USER'].indexOf(x.profileType.toUpperCase().trim()) !== -1
      );
      this.primaryUsersList = Array.from(this.facilityUsersList);
      this.backupUsersList = Array.from(this.facilityUsersList);
    });
  }

  /**
   * Get MonkeypoxAgreement if exists already
   */
  async getMonkeyPoxProviderAgreement() {
    this.isBusy = await this.monkeyPoxService.getMonkeyPoxProviderAgreement(this.facilityId, 'ACTIVE').then((response) => {
      if (response.status === 'SUCCESS' || response.status === 'TRN_WARNING') {
        if (response.objects && response.objects.length) {
          this.existingMonkeypoxAgree = response.objects[0].json;
          this.f.agreement_date.setValue(this.helperService.formatDate(this.existingMonkeypoxAgree[0].agreementDate));
          this.f.agreeInit.setValue(this.existingMonkeypoxAgree[0].agreementFullName);
          this.f.agreement_flag.setValue(this.existingMonkeypoxAgree[0].agreementFlag);
          this.f.primaryMonkeypoxCoordinator.setValue(this.existingMonkeypoxAgree[0].priContactIdSeq);
          this.f.backupMonkeypoxCoordinator.setValue(this.existingMonkeypoxAgree[0].bkpContactIdSeq);
          this.disableForm = true;
          if(this.action != CONSTANTS.UPDATE){
          this.action= CONSTANTS.REVIEW;
          }
          this.enableDisableForm();
        } else {
          this.enableDisableForm();
          this.initAgreementModel();
          this.setFormData();
          this.disableForm = true;
        }
      }
    });
  }

  async submitMonkeypoxAgreement() {
    this.isSubmit = true;

    if (this.monkeypoxAgreeFrm.status === 'INVALID') {
      return;
    }

    const request = {
      action: 'ADD',
      facilityId: this.facilityId,
      agreementFlag: this.f.agreement_flag ? 'Y' : '',
      agreementDate: this.f.agreement_date.value,
      agreementFullName: this.f.agreeInit.value,
      primaryContactIdSeq: this.f.primaryMonkeypoxCoordinator.value,
      backupContactIdSeq: this.f.backupMonkeypoxCoordinator.value,
    };

    const result = await lastValueFrom(this.apiService.post(CONFIG.MONKEYPOX_MANAGE_PROV_AGMT, request));
    if (result.status === 'SUCCESS' || result.status === 'TRN_WARNING') {
      this.helperService.showSuccessMessage('Monkeypox Provider Agreement submitted successfully', 'success');
      this.resetMonkeypoxAgreement();
      this.getMonkeyPoxProviderAgreement();
    } else {
      this.helperService.showErrorMessage(result.errorMessage);
    }

    return result;
  }

  /**
   * On selecting Primary Monkeypox Coordinator, remove the User from the Backup Coordinator
   */
  primaryMonkeypoxCoordinatorChange() {
    const primaryMonkeypoxCoordinator = this.f.primaryMonkeypoxCoordinator.value;

    this.backupUsersList = this.filterUserList(primaryMonkeypoxCoordinator);

    this.setOrClearRequiredValidators();
  }

  /**
   * On selecting Backup Coordinator, remove the User from the Primary Monkeypox Coordinator
   */
  backupMonkeypoxCoordinatorChange() {
    const backupMonkeypoxCoordinator = this.f.backupMonkeypoxCoordinator.value;

    this.primaryUsersList = this.filterUserList(backupMonkeypoxCoordinator);

    this.setOrClearRequiredValidators();
  }

  /**
   * set or clear required validators for the fields
   */
  setOrClearRequiredValidators() {
    this.isRequired =
      !this.helperService.IsNullOrEmpty(this.f.primaryMonkeypoxCoordinator.value) ||
      !this.helperService.IsNullOrEmpty(this.f.backupMonkeypoxCoordinator.value);

    if (this.isRequired) {
      this.f.primaryMonkeypoxCoordinator.setValidators(Validators.required);
      this.f.backupMonkeypoxCoordinator.setValidators(Validators.required);
    } else {
      this.f.primaryMonkeypoxCoordinator.clearValidators();
      this.f.backupMonkeypoxCoordinator.clearValidators();
    }

    this.f.primaryMonkeypoxCoordinator.updateValueAndValidity();
    this.f.backupMonkeypoxCoordinator.updateValueAndValidity();
  }

  /**
   * Returns list of users not matching the userIdSeq
   * @param userIdSeq userIdSeq to check from the list of users
   */
  filterUserList(userIdSeq) {
    return this.facilityUsersList.filter((user) => {
      return user.idSeq !== userIdSeq;
    });
  }

  /**
   * set the form data
   * @param vaccineCoordinatorData vaccineCordinator data
   */
  setFormData(vaccineCoordinatorData?) {
    if (vaccineCoordinatorData) {
      this.origVaccCoordinator = vaccineCoordinatorData;
    }
    this.f.primaryMonkeypoxCoordinator.setValue(this.origVaccCoordinator.primaryUserIdSeq?.toString());
    this.primaryMonkeypoxCoordinatorChange();
    this.f.backupMonkeypoxCoordinator.setValue(this.origVaccCoordinator.backupUserIdSeq?.toString());
    this.backupMonkeypoxCoordinatorChange();

    const checkbox = document.getElementById('agreement_flag') as HTMLInputElement | null;
    checkbox.checked = false;
    this.f.agreeInit.setValue(this.user.firstName + ' ' + this.user.lastName);
    this.f.agreement_date.setValue(this.helperService.formatDate(new Date()));
    this.monkeypoxAgreeFrm.markAsPristine();
    this.monkeypoxAgreeFrm.markAsUntouched();
  }

  /**
   * check if form is modified
   */
  checkIfFormIsModified() {
    return (
      this.origVaccCoordinator.primaryUserIdSeq !== this.helperService.isValue(this.f.primaryMonkeypoxCoordinator.value) ||
      this.origVaccCoordinator.backupUserIdSeq !== this.helperService.isValue(this.f.backupMonkeypoxCoordinator.value)
    );
  }
  /**
   * Reset the form to its original state
   */
  resetMonkeypoxAgreement() {
    this.isSubmit = false;
    this.helperService.resetForm(this.monkeypoxAgreeFrm);
    // this.setFormData();
    this.monkeypoxAgreeFrm.markAsPristine();
    this.monkeypoxAgreeFrm.markAsUntouched();
  }

  /**
   * print html
   * @param contentHtml html
   */
  print(contentHtml) {
    
    let popupWinindow
    //let innerContents = document.getElementById('permit').innerHTML;
    popupWinindow = window.open('', '_blank', 'width=900,height=900');
    popupWinindow.document.open();
    popupWinindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <link rel="stylesheet" type="text/css" href="styles.css" />
        <style>
       .body{
       width:900px;
       height:1200px;
       }
       </style>
        <title>Review Page</title>
        <script src="http://code.jquery.com/jquery-2.1.0.min.js"></script>
        <script>
          window.onload = function() {
            setTimeout(() => {
              window.print();
            }, 1000);      
          };
        </script>
      </head>
      <body id="printContent">
        ${contentHtml}
      </body>
    </html>
    `);
    popupWinindow.document.close();
    // let html = null;
    // html = `<!DOCTYPE html><html>`;
    // html += `<head>${document.head.innerHTML}</head>`;
    // html += `<body onload='window.print();' onfocus='setTimeout( function () {window.close();}, 2000)'>${contentHtml}</body></html>`;
    // //console.log(html);
    // const WindowPrt = window.open('', '', 'left=0,top=0,width=900,height=900,toolbar=0,scrollbars=0,status=0');
    // WindowPrt.document.write(html);
    // WindowPrt.document.close();
    // WindowPrt.focus();
  }

  /**
   * print form content
   * @param formId form id
   * @param additionalHtml additional html to be added above the html
   */
  printForm(formId, additionalHtml?) {
    const formHtml = document.getElementById(formId).innerHTML;
    if (additionalHtml) {
      this.print(`${additionalHtml}${formHtml}`);
    } else {
      this.print(formHtml);
    }
  }

  /**
   * Determine if form is valid
   */
  isFormValid() {
    return this.monkeypoxAgreeFrm.status === 'DISABLED' || this.monkeypoxAgreeFrm.valid;
  }

  /**
   * enable or disable form
   */
  enableDisableForm() {
    if (this.action === CONSTANTS.UPDATE) {
      this.monkeypoxAgreeFrm.disable();
      this.f.primaryMonkeypoxCoordinator.enable();
      this.f.backupMonkeypoxCoordinator.enable();
    } else if (this.action === CONSTANTS.ADD)
    {
      this.monkeypoxAgreeFrm.enable();
      this.f.primaryMonkeypoxCoordinator.enable();
      this.f.backupMonkeypoxCoordinator.enable();
    }
    else{
      this.monkeypoxAgreeFrm.disable();
      this.f.primaryMonkeypoxCoordinator.disable();
      this.f.backupMonkeypoxCoordinator.disable();
    }
  }

  /**
   * determine if component canDeactivate, if any component is dirty, alert user of unsaved changes
   */
  canDeactivate() {
    return this.checkIfFormIsModified() && this.helperService.isUserLoggedIn;
  }

  navigateToHippocrates = function () {
    this.helperService.navigateToHippocrates();
  };
}
